export interface SubscriptionStatus {
  tier: 'free' | 'pro' | 'enterprise' | 'pioneer';
  status: 'inactive' | 'active' | 'trialing' | 'past_due' | 'cancelled';
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  cancelledAt?: string;
  hasActiveSubscription: boolean;
  isPioneer?: boolean;
  pioneerNumber?: number;
}

export interface PricingPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
}

class SubscriptionService {
  private readonly API_BASE = import.meta.env.VITE_API_URL || '';

  async getStatus(token: string): Promise<SubscriptionStatus> {
    const response = await fetch(`${this.API_BASE}/api/stripe/subscription-status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get subscription status');
    }

    return response.json();
  }

  async createCheckoutSession(token: string, billingCycle: 'monthly' | 'yearly'): Promise<{ sessionId: string; url: string }> {
    const response = await fetch(`${this.API_BASE}/api/stripe/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ billingCycle })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to create checkout session');
    }

    return response.json();
  }

  async createPortalSession(token: string): Promise<{ url: string }> {
    const response = await fetch(`${this.API_BASE}/api/stripe/create-portal-session`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to create portal session');
    }

    return response.json();
  }

  // Check if user has access to premium features
  hasAccess(status: SubscriptionStatus): boolean {
    // Pioneers get full access forever
    if (status.tier === 'pioneer') return true;
    return status.hasActiveSubscription || status.status === 'trialing';
  }

  // Check if user is a pioneer
  isPioneer(status: SubscriptionStatus): boolean {
    return status.tier === 'pioneer';
  }

  // Get days remaining in trial
  getTrialDaysRemaining(status: SubscriptionStatus): number | null {
    if (status.status !== 'trialing' || !status.trialEndsAt) {
      return null;
    }

    const trialEnd = new Date(status.trialEndsAt);
    const now = new Date();
    const diff = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  // Get days until subscription renewal
  getDaysUntilRenewal(status: SubscriptionStatus): number | null {
    if (!status.currentPeriodEnd) {
      return null;
    }

    const periodEnd = new Date(status.currentPeriodEnd);
    const now = new Date();
    const diff = periodEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  // Format price for display
  formatPrice(amount: number, interval: 'month' | 'year'): string {
    return `£${amount.toFixed(2)}/${interval === 'month' ? 'mo' : 'yr'}`;
  }

  // Get pricing details
  getPricingPlans(): { monthly: PricingPlan; yearly: PricingPlan } {
    return {
      monthly: {
        id: 'monthly',
        name: 'Monthly',
        price: 3.99,
        interval: 'month',
        features: [
          'Unlimited aspirations, goals & lists',
          'AI-powered insights & suggestions',
          'Cloud sync & backup',
          'Schedule optimization',
          'Smart notifications',
          'Priority support'
        ]
      },
      yearly: {
        id: 'yearly',
        name: 'Yearly',
        price: 39.99,
        interval: 'year',
        features: [
          'Unlimited aspirations, goals & lists',
          'AI-powered insights & suggestions',
          'Cloud sync & backup',
          'Schedule optimization',
          'Smart notifications',
          'Priority support',
          'Save £7.89 vs monthly'
        ]
      }
    };
  }
}

export const subscriptionService = new SubscriptionService();
